import { View, Text, styled, ScrollView } from 'tamagui'
import { useState } from 'react'
import { useAppStore } from '@/store'
import { roles } from '@/mocks/roles'
import { Permission } from '@/types'
import type { Role } from '@/types'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { flexDirection: 'row', flex: 1 })

const RoleList = styled(View, {
  width: 200, borderRightWidth: 1, borderRightColor: '$borderDefault', padding: 12, gap: 4,
})

const RoleItem = styled(View, {
  flexDirection: 'row', alignItems: 'center', height: 36, paddingHorizontal: 12,
  borderRadius: '$borderRadiusMd', gap: 8, cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
  variants: { active: { true: { backgroundColor: '$sidebarActive' } } } as const,
})

const RoleColorDot = styled(View, {
  width: 12, height: 12, borderRadius: '$borderRadiusMd',
})

const RoleName = styled(Text, {
  fontFamily: '$fontBody', fontSize: 14, color: '$surfaceForeground',
})

const PermissionList = styled(View, {
  flex: 1, padding: 24, gap: 8,
})

const PermRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', height: 36, gap: 12,
})

const Checkbox = styled(View, {
  width: 18, height: 18, borderRadius: '$borderRadiusSm', borderWidth: 1, borderColor: '$borderDefault',
  alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  variants: { checked: { true: { backgroundColor: '$accent', borderColor: '$accent' } } } as const,
})

const PermLabel = styled(Text, {
  fontFamily: '$fontBody', fontSize: 14, color: '$surfaceForeground',
})

const permissionLabels: Record<Permission, string> = {
  [Permission.ManageServer]: 'Manage Server',
  [Permission.ManageChannels]: 'Manage Channels',
  [Permission.ManageRoles]: 'Manage Roles',
  [Permission.ManageMembers]: 'Manage Members',
  [Permission.ManageInvites]: 'Manage Invites',
  [Permission.KickMembers]: 'Kick Members',
  [Permission.BanMembers]: 'Ban Members',
  [Permission.SendMessages]: 'Send Messages',
  [Permission.ManageMessages]: 'Manage Messages',
  [Permission.EmbedLinks]: 'Embed Links',
  [Permission.AttachFiles]: 'Attach Files',
  [Permission.MentionEveryone]: 'Mention Everyone',
  [Permission.Connect]: 'Connect to Voice',
  [Permission.Speak]: 'Speak in Voice',
  [Permission.MuteMembers]: 'Mute Members',
  [Permission.DeafenMembers]: 'Deafen Members',
  [Permission.MoveMembers]: 'Move Members',
}

export function RoleSettings() {
  const { activeServerId } = useAppStore()
  const serverRoles = roles[activeServerId || ''] || []
  const [selectedRole, setSelectedRole] = useState<Role | null>(serverRoles[0] || null)

  return (
    <Container>
      <Header>
        <Title>Roles</Title>
      </Header>
      <Body>
        <RoleList>
          {serverRoles.map((role) => (
            <RoleItem key={role.id} active={selectedRole?.id === role.id} onPress={() => setSelectedRole(role)}>
              <RoleColorDot backgroundColor={role.color} />
              <RoleName>{role.name}</RoleName>
            </RoleItem>
          ))}
        </RoleList>
        <ScrollView flex={1}>
          <PermissionList>
            {selectedRole && (
              <>
                <Text fontFamily="$fontHeading" fontWeight="600" fontSize={16} color="$surfaceForeground" marginBottom={8}>
                  {selectedRole.name} Permissions
                </Text>
                {Object.values(Permission).map((perm) => (
                  <PermRow key={perm}>
                    <Checkbox checked={selectedRole.permissions.includes(perm)}>
                      {selectedRole.permissions.includes(perm) && (
                        <Text color="$surface" fontSize={12} fontWeight="700">✓</Text>
                      )}
                    </Checkbox>
                    <PermLabel>{permissionLabels[perm]}</PermLabel>
                  </PermRow>
                ))}
              </>
            )}
          </PermissionList>
        </ScrollView>
      </Body>
    </Container>
  )
}
